import React, { Component } from 'react';
import { connect } from 'react-redux';
import { getInstrumentData } from '../ReportRenderer/ReportRenderer.actions';
import copy from 'copy-to-clipboard';
import Utils from '../../../common/utils/Utils';
import TableEnhanced from './TableEnhanced';

const determineVisiblePageData = (data, activePage = 0, pageSize = 10) => {
  const begin = activePage * pageSize;
  const end = activePage * pageSize + pageSize;
  return data.slice(begin, end);
};

const constructFiltersArray = (filters, key, type, value) => {
  // If, for a specific column, the value passed is an empty string,
  // Then we remove that column from the filters array
  if (value === '') {
    return filters.filter((f) => f.key !== key);
  }

  const foundFilterIndex = filters.findIndex((f) => f.key === key);

  if (foundFilterIndex !== -1) {
    // We have found the filter in the filters array
    // We need to update its value
    return filters.map((f) => {
      if (f.key !== key) {
        return f;
      }

      return {
        key,
        type,
        value,
      };
    });
  } else {
    // We don't have the filter in the filters array
    // We need to add it
    return [
      ...filters,
      {
        key,
        type,
        value,
      },
    ];
  }
};

const processValueForCompoundKey = (el, key, type) => {
  if (el === undefined || el === null) {
    return undefined;
  }

  if (key.indexOf('.') === -1) {
    switch (type) {
      case 'string':
      case 'number':
        return el[key];
      case 'date':
        return new Date(el[key]).getTime();
      default:
        return el[key];
    }
  }

  const arr = key.split('.');
  const nextEl = el[arr.shift()];
  return processValueForCompoundKey(nextEl, arr.join('.'));
};

const filterData = (data, filtersArray, columns, defaultValueFormatter) => {
  let filteredData = data.slice(0);

  if (!filtersArray.length) {
    return filteredData;
  }

  filtersArray.forEach((f) => {
    let afterFilter = filteredData.filter((el) => {
      const val = processValueForCompoundKey(el, f['key']);
      const type = f['type'] || null;

      if (val === undefined) {
        return false;
      }

      if (type === 'number') {
        return +defaultValueFormatter(val, type) === +f['value'];
      }

      if (
        type === 'string' ||
        type === 'boolean' ||
        type === 'date' || 
        type === 'datetime' ||
        type === 'duration' ||
        type === null ||
        val === null
      ) {
        return (defaultValueFormatter(`${val}`, type).toLowerCase().indexOf(f['value'].toLowerCase()) !== -1);
      }

      if (type === 'markup') {
        return (defaultValueFormatter(`${val}`, null).toLowerCase().indexOf(f['value'].toLowerCase()) !== -1);
      }

      // Always include the element outside of the specified cases
      return true;
    });

    //search for elements which have quickFilterKeywords action defined
    let dataWithQuickFilter = afterFilter.filter((el) => el.quickFilterKeywords);

    if (dataWithQuickFilter) {
      for (var i = 0; i < dataWithQuickFilter.length; i++) {
        let elWithQuickFilterKeywords = dataWithQuickFilter[i];
        let insertIndex = afterFilter.findIndex((el) => el == elWithQuickFilterKeywords);
        //append the properties fron quickFilterKeywords to the filtered elements
        afterFilter[insertIndex] = {
          ...elWithQuickFilterKeywords,
          ...elWithQuickFilterKeywords.quickFilterKeywords,
        };

        //include in the total calculations in case it was excluded
        if (afterFilter[insertIndex].skipFooter && afterFilter[insertIndex].skipFooter == true) {
          afterFilter[insertIndex].skipFooter = false;
        }
      }
    }
    filteredData = afterFilter;
  });

  return filteredData;
};

const sortData = (data, sortColumn, sortOrder, type) => {
  return data
    .sort((a, b) => {
      let aVal = processValueForCompoundKey(a, sortColumn, type);
      let bVal = processValueForCompoundKey(b, sortColumn, type);

      if (type === 'number') {
        aVal = aVal || -Infinity;
        bVal = bVal || -Infinity;
      } else {
        aVal = aVal || '';
        bVal = bVal || '';
      }

      if (sortOrder === 'asc') {
        if (aVal === bVal) {
          return 0;
        }

        return aVal < bVal ? -1 : 1;
      } else {
        if (aVal === bVal) {
          return 0;
        }

        return aVal > bVal ? -1 : 1;
      }
    })
    .slice(0);
};

const constructTableFooter = (data, columns, defaultValueFormatter) => {
  const footerObject = {};
  const interestCols = columns.map((col) => ({
    key: col.key,
    footer: col.footer,
    numerator: col.numerator,
    denominator: col.denominator,
  }));
  interestCols.forEach((col) => {
    switch (col.footer) {
      case 'Sum':
        footerObject[col.key] = sumColumn(data, col.key);
        break;
      case 'Perc':
        const sumNumerator = sumColumn(data, col.numerator);
        const sumDenominator = sumColumn(data, col.denominator);
        footerObject[col.key] = percentageCalc(sumNumerator, sumDenominator);
        break;
      default:
        footerObject[col.key] = col.footer;
        break;
    }
  });
  return footerObject;
};

const sumColumn = (data, key) => {
  let sum = 0;
  data.forEach((obj) => {
    if (obj[key] != null && (obj['skipFooter'] === undefined || obj['skipFooter'] === false)) {
      sum += obj[key];
    }
  });

  return sum;
};

const percentageCalc = (numerator, denominator) => {
  if (numerator !== null && denominator !== null && denominator != 0) {
    return (numerator / denominator) * 100;
  } else {
    return 0;
  }
};

class TableEnhancedContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data:
        determineVisiblePageData(
          props.data,
          props.activePage,
          props.pageSize || parseInt(props.globalSettings.RowsPerTablePage)
        ) || [],
      dataFull: props.data.slice(0),
      footerData: this.props.hasFooter
        ? constructTableFooter(props.data, props.columns, this._valueFormatter)
        : null,
      filteredData: props.data.slice(0),
      filtersArray: [],
      columns: props.columns.slice(0),
      activePage: props.activePage || 0,
      pageSize: props.pageSize || parseInt(props.globalSettings.RowsPerTablePage),
      totalItems: props.data.length || 0,
      sortColumn: '',
      sortOrder: 'asc',
      hasToPrint: false,
      openShowHideColumns: false,
      anchorElForShowHideColumns: null,
    };

    this.hasPaginationEventsBound = false;

    this._toggleColumn = this._toggleColumn.bind(this);
    this._onClickCopy = this._onClickCopy.bind(this);
    this._onClickPrint = this._onClickPrint.bind(this);
    this._onClickExport = this._onClickExport.bind(this);
    this._onClickChangePage = this._onClickChangePage.bind(this);
    this._onClickSortColumn = this._onClickSortColumn.bind(this);
    this._onChangePageSize = this._onChangePageSize.bind(this);
    this._onFilterColumn = this._onFilterColumn.bind(this);
    this._valueFormatter = this._valueFormatter.bind(this);
    this._onClickShowHideColumns = this._onClickShowHideColumns.bind(this);
    this._onRequestCloseShowHideColumns = this._onRequestCloseShowHideColumns.bind(this);
  }

  componentWillReceiveProps(newProps, oldProps) {
    if (newProps.data !== oldProps.data || newProps.columns !== oldProps.columns) {
      const dataFull = newProps.data.slice(0);
      const columns = newProps.columns.slice(0);

      var sortColumn = this.state.sortColumn;
      var sortOrder = this.state.sortOrder;
      var newData = newProps.data;

      var defaultSort = this.state.columns.find((el) => el.sort == 'asc' || el.sort == 'desc');
      if (defaultSort) {
        sortColumn = defaultSort.key;
        sortOrder = defaultSort.sort;

        newData = sortData(dataFull, sortColumn, sortOrder, defaultSort.type);
      }

      const filteredData = filterData(
        newData,
        this.state.filtersArray,
        columns,
        this._valueFormatter
      );
      const footerData = newProps.hasFooter
        ? constructTableFooter(filteredData, columns, this._valueFormatter)
        : null;

      let { activePage, pageSize } = this.state;
      if (activePage * pageSize > filteredData.length) {
        activePage = 0;
      }

      const data = determineVisiblePageData(filteredData, activePage, pageSize);

      this.setState({
        dataFull,
        filteredData,
        data,
        footerData,
        columns,
        activePage,
        totalItems: dataFull.length,
        sortColumn,
        sortOrder,
      });
    }

    if (!this.hasPaginationEventsBound && newProps.data.length) {
      setTimeout(() => {
        const links = document.querySelectorAll(
          '.table-enhanced__pagination-wrapper .ant-pagination li'
        );
        for (let i = 0; i < links.length; ++i) {
          links[i].title = '';
        }
      }, 0);
    }
  }

  componentDidMount() {
    var columnConfig = this.state.columns.find((el) => el.sort == 'asc' || el.sort == 'desc');
    if (columnConfig) {
      var sortColumn = columnConfig.key;
      var sortOrder = columnConfig.sort;

      const filteredData = sortData(this.state.dataFull, sortColumn, sortOrder, columnConfig.type);

      const { activePage, pageSize } = this.state;
      const data = determineVisiblePageData(filteredData, activePage, pageSize);

      this.setState({
        filteredData,
        data,
        sortColumn,
        sortOrder,
      });
    }
  }

  render() {
    return (
      <TableEnhanced
        {...this.props}
        {...this.state}
        toggleColumn={this._toggleColumn}
        onClickCopy={this._onClickCopy}
        onClickPrint={this._onClickPrint}
        onClickExport={this._onClickExport}
        onClickChangePage={this._onClickChangePage}
        onClickSortColumn={this._onClickSortColumn}
        onChangePageSize={this._onChangePageSize}
        onFilterColumn={this._onFilterColumn}
        hasToPrint={this.state.hasToPrint}
        defaultValueFormatter={this._valueFormatter}
        onClickShowHideColumns={this._onClickShowHideColumns}
        onRequestCloseShowHideColumns={this._onRequestCloseShowHideColumns}
      />
    );
  }

  _valueFormatter(val, type) {
    if (val === null) {
      return '';
    }

    if ((!type && typeof val === 'number') || type === 'number') {
      return Utils.FormatNumber(val, this.props.globalSettings);
    }

    if ((!type && Utils.IsDateTime(val)) || type === 'datetime') {
      return Utils.FormatDateTime(val, this.props.globalSettings);
    }

    if ((!type && Utils.IsDateTime(val)) || type === 'date') {
      return Utils.FormatDate(val, this.props.globalSettings);
    }

    if (type === 'duration') {
      return Utils.FormatDuration(val, this.props.globalSettings);
    }

    return typeof val === 'string' || typeof val === 'boolean' ? `${val}` : null;
  }

  _onClickSortColumn(col, type) {
    let sortColumn = col;
    let sortOrder;

    if (col === this.state.sortColumn) {
      sortOrder = this.state.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      sortOrder = 'asc';
    }

    const filteredData = sortData(this.state.filteredData, sortColumn, sortOrder, type);
    const { activePage, pageSize } = this.state;
    const data = determineVisiblePageData(filteredData, activePage, pageSize);

    this.setState({
      filteredData,
      data,
      sortColumn,
      sortOrder,
    });
  }

  _toggleColumn(columnKey) {
    const newColsConfig = this.state.columns.slice(0);
    const colIndex = newColsConfig.findIndex((col) => col.key === columnKey);
    newColsConfig[colIndex]['visible'] = !newColsConfig[colIndex]['visible'];
    this.setState({
      columns: newColsConfig,
    });
  }

  _onClickCopy() {
    const spacer = '    ';
    const filteredColumns = this.state.columns.filter(
      (col) =>
        col.visible &&
        col.key.toLowerCase() !== 'actions' &&
        col.key.toLowerCase() !== 'expandable' &&
        col.key.toLowerCase() !== 'subreportdialog'
    );
    const colNames = filteredColumns.map((col) => col.name);
    const colKeys = filteredColumns.map((col) => col.key);
    const headers = colNames.join(spacer);

    const data = this.state.filteredData
      .map((el) => {
        const values = colKeys
          .map((col) => {
            const val = processValueForCompoundKey(el, col);
            return val === undefined ? '' : val;
          })
          .join(spacer);
        return `${values}\n`;
      })
      .join('');

    const footer = this.props.hasFooter
      ? colKeys.map((key) => this.state.footerData[key]).join(spacer)
      : '';

    const finalCopyString = `${headers}\n${data}${footer}`;
    copy(finalCopyString);
  }

  _onClickPrint() {
    this.setState(
      () => ({ hasToPrint: true }),
      () => {
        const tableHead = document
          .querySelectorAll('.table-enhanced__wrapper__print table')[0]
          .querySelector('thead');
        const tableBody = document.querySelector('.table-enhanced__print-wrapper tbody');
        const tableFooter = document.querySelector('.table-enhanced__footer__print');
        const headStyles = `
          <style>
            table {
              width: 720px !important;
              table-layout: auto !important;
              background-color: rgb(255, 255, 255);
              width: 100% !important;
              border-collapse: collapse !important;
              border-spacing: 0px !important;
              font-family: Roboto, sans-serif !important;
            }
            table th,
            table td {
              white-space: normal !important;
              padding-left: 8px !important;
              padding-right: 8px !important;
            }
            table th .icon-sort-wrapper, 
            table .table-filters,
            table .table-actions { display: none !important; }
            table tr { display: table-row !important; }
          </style>
        `;

        const finalString = `
          ${headStyles}
          <table>        
            ${tableHead.outerHTML}
            ${tableBody.outerHTML}
            ${tableFooter ? tableFooter.outerHTML : ''}
          </table>
        `;

        window.w = window.open();
        window.w.document.write(finalString);
        window.w.print();
        window.w.close();

        this.setState({ hasToPrint: false });
      }
    );
  }

  _onClickExport(exportRawData) {
    let filteredCols = this.state.columns.filter(
      (col) =>
        col.visible &&
        col.key.toLowerCase() !== 'actions' &&
        col.key.toLowerCase() !== 'expandable' &&
        col.key.toLowerCase() !== 'subreportdialog'
    );

    if (exportRawData) {
      let config = this.props.extraActionsButtons.find(el => el.extraActionsButtons == true);
      let exportReportName = config.extraActionsButtonList.find(el => el.key == "exportWithRawData").reportName;
      let newReportQuery = {
        ReportName: exportReportName,
        SelectedInstrument: this.props.instrumentInfo,
        StartDate: Utils.FeDateAsUtcForBeISOString(this.props.query.StartDate),
        EndDate: Utils.FeDateAsUtcForBeISOString(this.props.query.EndDate),
      };

      getInstrumentData(newReportQuery)
        .then((res) => {
          const resTable = Object.assign(
            {},
            res.body.find((config) => config.Name === 'Table')
          );

          filteredCols.push(resTable.Configuration.columnsConfig.find((col) => col.rawdata));

          let keys = filteredCols.map((col) => col.key);

          let filteredData = filterData(
            resTable.Data,
            this.state.filtersArray,
            filteredCols,
            this._valueFormatter
          );

          filteredData = sortData(
            filteredData,
            this.state.sortColumn,
            this.state.sortOrder,
            undefined
          );

          this.exportToCSV(filteredCols, keys, filteredData);
        })
        .catch((e) => {
          this.setState({
            isFetching: false,
          });
        });
    } else {
      let keys = filteredCols.map((col) => col.key);

      this.exportToCSV(filteredCols, keys, this.state.filteredData);
    }
  }

  exportToCSV(filteredCols, keys, filteredData) {
    const headers = [];
    headers.push(['User name', this.props.userInfo]);
    headers.push(['SW version', this.props.versionInfo]);
    headers.push([
      'Date and time of export',
      Utils.FormatDateTime(new Date(), this.props.globalSettings),
    ]);
    headers.push(['Exported page URL', window.location]);

    if (this.props.instrumentInfo && this.props.instrumentInfo !== 'N/A') {
      headers.push(['Instrument serial number', this.props.instrumentInfo]);
    }

    const filters = filteredCols
      .filter((c) => !c.rawdata)
      .map(
        (col) => (this.state.filtersArray.find((f) => f.key === col.key) || { value: '' }).value
      );
    filters.unshift('Applied filters');
    headers.push(filters);

    if (this.props.hasFooter) {
      const footer = filteredCols.map((col) =>
        this.state.footerData &&
        (this.state.footerData[col.key] || this.state.footerData[col.key] === 0)
          ? this.state.footerData[col.key]
          : ''
      );
      footer.unshift('Summary');
      headers.push(footer);
    }

    const columnNames = filteredCols.map((col) => col.name);
    columnNames.unshift('Column name');
    headers.push(columnNames);

    const data = filteredData.map((el, i) => {
      const row = keys.map((k) => {
        const val = processValueForCompoundKey(el, k);
        return val === undefined ? '' : val;
      });
      row.unshift(i + 1);
      return row;
    });
    Utils.exportToCsv('data.csv', [...headers, ...data]);
  }

  _onClickChangePage(activePage) {
    if (activePage !== this.state.activePage) {
      const { pageSize } = this.state;
      const data = determineVisiblePageData(this.state.filteredData, activePage, pageSize);
      this.setState({
        activePage,
        data,
      });
    }
  }

  _onChangePageSize(pageSize) {
    // Always go back to page one when changing number of items per page
    const activePage = 0;
    const data = determineVisiblePageData(this.state.filteredData, activePage, pageSize);
    this.setState({ data, pageSize, activePage });
  }

  _onFilterColumn(key, type, val) {
    const filtersArray = constructFiltersArray(this.state.filtersArray, key, type, val);
    const filteredData = filterData(
      this.state.dataFull,
      filtersArray,
      this.state.columns,
      this._valueFormatter
    );
    const footerData = this.props.hasFooter
      ? constructTableFooter(filteredData, this.state.columns, this._valueFormatter)
      : null;
    // Always go back to page one when changing number of items per page
    const activePage = 0;
    const { pageSize } = this.state;
    const data = determineVisiblePageData(filteredData, activePage, pageSize);
    this.setState({
      filteredData,
      filtersArray,
      data,
      footerData,
      activePage,
      totalItems: filteredData.length,
    });
  }

  _onClickShowHideColumns = (e) => {
    e.preventDefault();
    this.setState({
      openShowHideColumns: true,
      anchorElForShowHideColumns: e.currentTarget,
    });
  };

  _onRequestCloseShowHideColumns() {
    this.setState({ openShowHideColumns: false, anchorElForShowHideColumns: null });
  }
}

const mapStateToProps = (state) => ({
  globalSettings: state.user ? state.user.Settings : undefined,
  userInfo: state.user ? state.user.User.Username : undefined,
  versionInfo: state.version ? window.dashboardVersion + '/' + state.version.content : undefined,
});

export default connect(mapStateToProps)(TableEnhancedContainer);
